import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {isTracker} from '../../../../../../helpers/modelHelpers';
import {isTeammateNamespace} from '../../../../../../helpers/models/namespaceHelpers';
import {makeMapStateToProps} from '../../../../../../helpers/reselectHelpers';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {AppState} from '../../../../../../store/appState';
import {getHasPermission} from '../../../../../../store/data/access/accessSelectors';
import {
  getCanAutocompleteMentionContacts,
  getCanInviteVisitors,
  getCompany,
} from '../../../../../../store/data/company/companySelectors';
import {
  makeGetConversationInboxes,
  makeGetConversationInboxTeammateIds,
} from '../../../../../../store/data/conversations/conversationsSelectors';
import {inviteToConversationAction} from '../../../../../../store/data/conversations/inviteToConversationAction';
import {updateDataAction} from '../../../../../../store/data/dataGroupsActions';
import {getUserOwnedInboxes} from '../../../../../../store/data/inboxes/inboxesSelectors';
import {
  getMentionableAliases,
  getMentionableEmails,
  makeGetIsTeamMemberForConversations,
  makeGetMentionableTeammatesForConversations,
  TeammatesSelector,
} from '../../../../../../store/data/teammates/mentionTeammatesSelectors';
import {getFindOnlineStatus} from '../../../../../../store/data/teammates/onlineTeammatesSelectors';
import {getUser} from '../../../../../../store/data/teammates/teammatesSelectors';
import {
  getHasAutoShareDrafts,
  getHasLazyTeammatesDropdowns,
} from '../../../../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {makeGetInviteParticipantsTrackers} from '../../../../../../store/stateless/inviteParticipantsSelectors';
import {InviteDropdownInvite} from './inviteDropdownInvite';

/*
 * Props.
 */

interface InviteDropdownInviteContainerProps {
  cellsContext: CellsContext;
  conversation: ConversationModel;
}

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: InviteDropdownInviteContainerProps) {
  return props.conversation;
}

function makeGetConversations() {
  return createSelector(getConversation, (conversation) => [conversation]);
}

function makeGetTeammateSuggestions(
  getMentionableTeammates: TeammatesSelector<InviteDropdownInviteContainerProps>,
) {
  return createSelector([getConversation, getMentionableTeammates], (conversation, mentionableTeammates) =>
    mentionableTeammates.filter((t) => !isTracker(conversation.trackers[t.id])),
  );
}

function makeGetCanComment() {
  return createSelector([getHasPermission, getConversation], (hasPermission, conversation) =>
    hasPermission(conversation.namespace, PermissionsEnum.CONVERSATION_COMMENT),
  );
}

function makeGetConversationInboxIds() {
  return createSelector([getConversation, getUserOwnedInboxes], (conversation, userOwnedInboxes) => {
    if (isTeammateNamespace(conversation.namespace)) {
      return userOwnedInboxes.map((inbox) => inbox.id);
    }

    return conversation.inboxIds;
  });
}

function getUserOrDelegatedTeammateId(_state: unknown, props: {cellsContext: CellsContext}) {
  return props.cellsContext.teammateId;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InviteDropdownInviteContainerProps>()(() => {
  const getConversations = makeGetConversations();
  const getConversationInboxes = makeGetConversationInboxes(getConversation);
  const getConversationInboxTeammateIds = makeGetConversationInboxTeammateIds(getConversationInboxes);
  const getMentionableTeammates = makeGetMentionableTeammatesForConversations(getConversations);
  const getTrackers = makeGetInviteParticipantsTrackers(
    getUserOrDelegatedTeammateId,
    getConversationInboxTeammateIds,
    getConversation,
  );

  return {
    company: getCompany,
    teammates: getMentionableTeammates,
    existingTrackers: getTrackers,
    mentionableAliases: getMentionableAliases,
    mentionableEmails: getMentionableEmails,
    canInviteVisitors: getCanInviteVisitors,
    canAutocompleteMentionContacts: getCanAutocompleteMentionContacts,
    teammateSuggestions: makeGetTeammateSuggestions(getMentionableTeammates),
    isTeamMember: makeGetIsTeamMemberForConversations(getConversations),
    canComment: makeGetCanComment(),
    findOnlineStatus: getFindOnlineStatus,
    hasLazyTeammatesDropdowns: getHasLazyTeammatesDropdowns,
    user: getUser,
    conversationInboxIds: makeGetConversationInboxIds(),
    hasAutoShareDrafts: getHasAutoShareDrafts,
  };
});

export const InviteDropdownInviteContainer = connect(
  mapStateToProps,
  {inviteToConversationAction, updateDataAction},
  (stateProps, dispatchProps, ownProps) => {
    const addTrackers = (
      trackers: ReadonlyArray<number | string>,
      commentText: string | undefined,
      trackerTeammates: ReadonlyArray<TeammateModel>,
    ) => {
      const {cellsContext, conversation} = ownProps;
      // Write the new teammates into Redux since the conversation will now depend on them.
      dispatchProps.updateDataAction({teammates: trackerTeammates});
      dispatchProps.inviteToConversationAction(cellsContext, conversation, trackers, commentText);
    };

    return {
      ...stateProps,
      ...ownProps,
      addTrackers,
    };
  },
)(InviteDropdownInvite);
